/**
 * Helper: Parse flexible parameter signature
 * @private
 */
function parseValidationParams(fieldNameOrOptions, options) {
  if (typeof fieldNameOrOptions === 'string') {
    return {
      fieldName: fieldNameOrOptions,
      validationOptions: options,
    };
  }
  return {
    fieldName: fieldNameOrOptions?.fieldName,
    validationOptions: fieldNameOrOptions ?? {},
  };
}

/**
 * Helper: Throw validation error
 * @private
 */
function throwValidationError(fieldName, validationErrors) {
  const error = new Error(`Validation failed for ${fieldName}`);
  error.validationErrors = validationErrors;
  throw error;
}

/**
 * Helper: Validate null/undefined values
 * @private
 */
function validateNullValue(value, fieldName, allowNull) {
  if (value === null || value === undefined) {
    if (!allowNull) {
      throwValidationError(fieldName, [`${fieldName} is required`]);
    }
    return true; // null is allowed
  }
  return false; // not null, continue validation
}

/**
 * Helper: Validate type is string
 * @private
 */
function validateType(value, fieldName) {
  if (typeof value !== 'string') {
    throwValidationError(fieldName, [`${fieldName} must be a string`]);
  }
}

/**
 * Helper: Collect length validation errors
 * @private
 */
function collectLengthErrors(value, fieldName, allowEmpty, minimumLength, maximumLength) {
  const errors = [];

  if (value === '' && !allowEmpty) {
    errors.push(`${fieldName} cannot be empty`);
  }

  if (value.length < minimumLength) {
    errors.push(
      `${fieldName} must be at least ${minimumLength} character${
        minimumLength !== 1 ? 's' : ''
      } long`
    );
  }

  if (value.length > maximumLength) {
    errors.push(
      `${fieldName} must be at most ${maximumLength} character${
        maximumLength !== 1 ? 's' : ''
      } long`
    );
  }

  return errors;
}

/**
 * Validates a string value against specified criteria
 * @param {*} value - The value to validate
 * @param {string|Object} fieldNameOrOptions - Either the field name (string) or options object
 * @param {Object} [options] - Validation options (only used if second param is a string)
 * @param {boolean} [options.allowNull=false] - Whether null/undefined values are allowed
 * @param {boolean} [options.allowEmpty=false] - Whether empty strings are allowed
 * @param {number} [options.minimumLength=0] - Minimum string length
 * @param {number} [options.maximumLength=Number.MAX_SAFE_INTEGER] - Maximum string length
 * @throws {Error} Throws an error with an array of validation failures
 */
export function validateString(value, fieldNameOrOptions, options = {}) {
  const { fieldName, validationOptions } = parseValidationParams(fieldNameOrOptions, options);

  const {
    allowNull = false,
    allowEmpty = false,
    minimumLength = 0,
    maximumLength = Number.MAX_SAFE_INTEGER,
  } = validationOptions;

  // Early return if null/undefined
  if (validateNullValue(value, fieldName, allowNull)) {
    return;
  }

  // Validate type
  validateType(value, fieldName);

  // Collect length validation errors
  const validationErrors = collectLengthErrors(
    value,
    fieldName,
    allowEmpty,
    minimumLength,
    maximumLength
  );

  // Throw if any errors
  if (validationErrors.length > 0) {
    throwValidationError(fieldName, validationErrors);
  }
}
